<!-- eslint-disable vue/valid-template-root -->
<script lang="ts" setup>
import type { GeoMap, LatLngLiteral } from '@@/bits/map'
import { NARNIA_LATLNG } from '@@/bits/map'
import { MapConnection } from '@@/bits/map_connection'
import { getConnectorColorsFromThemeId } from '@@/bits/map_theme'
import type { MapPostAttributes } from '@@/types'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  from: MapPostAttributes
  to: MapPostAttributes
  canDelete: boolean
  themeId: number
  label?: string
  labelCount?: number
  map?: GeoMap | null
}>()

const emit = defineEmits<{ (e: 'delete'): void }>()

const mapConnection = ref<MapConnection | null>(null)

const start = computed<LatLngLiteral | null>(() => {
  if (!props.from) return null
  if (!props.from.location_point || !props.from.location_point.latitude || !props.from.location_point.longitude)
    return NARNIA_LATLNG
  return {
    lat: props.from.location_point.latitude,
    lng: props.from.location_point.longitude,
  }
})

const end = computed<LatLngLiteral | null>(() => {
  if (!props.to) return null
  if (!props.to.location_point || !props.to.location_point.latitude || !props.to.location_point.longitude)
    return NARNIA_LATLNG
  return {
    lat: props.to.location_point.latitude,
    lng: props.to.location_point.longitude,
  }
})

watch(
  () => props.map,
  (map) => {
    if (map) {
      mapConnection.value?.setMap(map)
    }
  },
)

watch(
  [start, end],
  () => {
    if (start.value && end.value) {
      mapConnection.value?.setLocation(start.value, end.value)
    }
  },
  { immediate: true },
)

watch(
  () => props.canDelete,
  (canDelete) => {
    if (canDelete) {
      mapConnection.value?.showDeleteButton()
    } else {
      mapConnection.value?.hideDeleteButton()
    }
  },
)

watch([() => props.label, () => props.labelCount], () => {
  if (props.labelCount) {
    mapConnection.value?.setLabel(String(props.labelCount))
    mapConnection.value?.setLabelType('count')
  }
  if (props.label) {
    mapConnection.value?.setLabel(props.label)
  }
})

watch(
  () => props.themeId,
  () => {
    const { connectorLineColor, labelBackgroundColor, labelTextColor } = getConnectorColorsFromThemeId(props.themeId)
    mapConnection.value?.setColor(connectorLineColor)
    mapConnection.value?.setLabelColor({
      backgroundColor: labelBackgroundColor,
      foregroundColor: labelTextColor,
    })
  },
)

const deleteEvent = () => {
  emit('delete')
}

onMounted(() => {
  if (start.value && end.value) {
    mapConnection.value = new MapConnection(
      start.value,
      end.value,
      props.label || (props.labelCount && String(props.labelCount)) || '',
    )

    if (props.labelCount) {
      mapConnection.value.setLabelType('count')
    }
    mapConnection.value.addDeleteEventListener(() => {
      deleteEvent()
    })
    if (props.map) {
      mapConnection.value.setMap(props.map)
    }
    if (props.canDelete) {
      mapConnection.value.showDeleteButton()
    } else {
      mapConnection.value.hideDeleteButton()
    }
    const { connectorLineColor, labelBackgroundColor, labelTextColor } = getConnectorColorsFromThemeId(props.themeId)
    mapConnection.value.setColor(connectorLineColor)
    mapConnection.value.setLabelColor({
      backgroundColor: labelBackgroundColor,
      foregroundColor: labelTextColor,
    })
  }
})

onBeforeUnmount(() => {
  mapConnection.value?.destroy()
})
</script>

<template>
  <!-- This component does not render any DOM node -->
</template>

<style lang="scss">
@import '@assets/stylesheets/3/modules/all';
.map-connection-label {
  border-style: none;
  border-width: 0px;
  background: rgba(0, 0, 0, 1);
  color: white;
  font-size: 12px;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 4px;
  body[data-color-scheme='dark'] & {
    background: rgba(255, 255, 255, 0.75);
    color: black;
  }
  font-family: $sans-font-family;
  &.count {
    font-weight: 800;
    font-style: italic;
    text-align: center;
  }
}
</style>
